import React, { useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Image from "next/image";
import SearchIcon from "@mui/icons-material/Search";
import { useState, useRef } from "react";
import { Grid } from "@mui/material";
import { motion } from "framer-motion";
import Link from "next/link";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentStep,
  setSelected,
  setSelectedTab,
} from "@/redux/slices/Product";
import { getSearchedResults } from "@/redux/slices/GlobalSearch";
import { getCDNImageUrl, slugify } from "@/utils/Common";
import {
  trackSearchBarClicked,
  trackSearched,
  trackSearchResultClicked,
  getPageType,
} from "@/utils/gtmEvents";

const SearchMenu = ({ closeMemnu }) => {
  const [menuIndex, setMenuIndex] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchBarClicked, setSearchBarClicked] = useState(false);
  const result = useSelector((state) => state?.GlobalSearch?.result);
  const dispatch = useDispatch();
  const lastSearchQuery = useRef("");
  const searchMenus = [
    {
      title: "Water Treatment & Mining ",
      icon: "https://storage.googleapis.com/elchemy-dev/water_405884_NaeZsIw.svg",
    },
    {
      title: "Personal Care",
      icon: "https://storage.googleapis.com/elchemy-dev/Group_Otaaz38.svg",
    },
    {
      title: "C.A.S.E",
      icon: "https://storage.googleapis.com/elchemy-dev/adhesives_9759842_t2oLL1R.svg",
    },
    {
      title: "Chemical Intermediates",
      icon: "https://storage.googleapis.com/elchemy-dev/Group (1)_JmQxEdo.svg",
    },
    {
      title: "Oil and Gas",
      icon: "https://storage.googleapis.com/elchemy-dev/drilling-machine-drill_svgrepo.com.svg",
    },
    {
      title: "Food and Nutrition",
      icon: "https://storage.googleapis.com/elchemy-dev/1706613878_pyfnOrm.svg",
    },
    {
      title: "Flavours and Fragrance",
      icon: "https://storage.googleapis.com/elchemy-dev/1706614926_dAlDQr2.svg",
    },
    {
      title: "Home Care and Industrial Cleaning",
      icon: "https://storage.googleapis.com/elchemy-dev/Vector (1)_kzFy7Zq.svg",
    },
  ];

  const close = () => {
    closeMemnu();
  };

  function debounce(func, delay) {
    let timeoutId;
    return function (...args) {
      const context = this;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(function () {
        func.apply(context, args);
      }, delay);
    };
  }
  const handleInputChange = (e) => {
    const query = e?.target?.value;
    dispatch(getSearchedResults(query));

    if (query?.length > 0) {
      if (!isSearching) {
        setIsSearching(true);
      }
      // Store the query to track the last search
      lastSearchQuery.current = query;
    } else {
      if (isSearching) {
        setIsSearching(false);
      }
    }
  };

  const debouncedHandleInputChange = debounce(handleInputChange, 350);

  // Track SEARCHED event when results are returned
  useEffect(() => {
    if (lastSearchQuery.current && result) {
      const pageUrl = typeof window !== "undefined" ? window.location.href : "";
      const pageType = typeof window !== "undefined"
        ? getPageType(window.location.pathname)
        : "other";

      trackSearched(
        pageUrl,
        pageType,
        lastSearchQuery.current,
        result?.length || 0
      );
    }
  }, [result]);

  // Handle search bar click
  const handleSearchBarClick = () => {
    if (!searchBarClicked) {
      const pageUrl = typeof window !== "undefined" ? window.location.href : "";
      const pageType = typeof window !== "undefined"
        ? getPageType(window.location.pathname)
        : "other";

      trackSearchBarClicked(pageUrl, pageType);
      setSearchBarClicked(true);
    }
  };

  // Handle search result click
  const handleResultClick = (productName, index) => {
    trackSearchResultClicked(index + 1, productName);
    close();
  };

  useEffect(() => {
    dispatch(getSearchedResults());
    document.body.classList.add("no-scrollbar");
    return () => {
      document.body.classList.remove("no-scrollbar");
    };
  }, []);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: "-100%" }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 3, type: "spring", stiffness: 50, delay: 0 }}
        style={{ zIndex: "1000", position: "relative" }}
      >
        <section className="spacing_md bg-white relative z-100 h-[100vh] ">
          <div className="flex justify-between md:pl-16 ">
            <div>
              <Image
                className=""
                src={getCDNImageUrl("elchemylogo.svg")}
                alt="logo"
                width={119}
                height={44}
              />
            </div>
            <div onClick={close} className="cursor-pointer  ">
              <CloseIcon style={{ width: "30px", height: "30px" }} />
            </div>
          </div>
          <div className="flex flex-col items-center md:gap-[3rem] gap-[1.5rem] ">
            <div className="text-center md:text-[1.7rem]  text-[1.2rem] mt-8">
              What are you looking for?
            </div>

            <div className="md:w-[65%] w-[100%]  text-center relative searchInputBox  flex items-center justify-around p-2 ">
              <input
                className="w-[80%]"
                type="text"
                placeholder="Search Products"
                style={{ outline: "none" }}
                onChange={debouncedHandleInputChange}
                onClick={handleSearchBarClick}
              />
              <div className="w-[42px] h-[42px] rounded-[50%] bg-red  flex justify-center items-center ">
                <SearchIcon style={{ color: "white" }} />
              </div>
              {isSearching && result && (
                <div className="absolute max-h-[12rem] overflow-y-scroll  top-[120%] w-[100%] left-0 bg-white shadow-md pt-2 rounded-[4px]">
                  {result?.length > 0 ? (
                    <div>
                      {result?.map((e, index) => (
                        <Link
                          key={e?.id}
                          href={`/product/${slugify(e?.ChemicalName)}-${e?.id}`}
                          onClick={() => handleResultClick(e?.ChemicalName, index)}
                          className="py-1 my-1 cursor-pointer font-mukta block hover:bg-[#FFEBEA] "
                        >
                          <div className="w-full flex justify-between items-center px-3 md:px-10 ">
                            <p>{`${e?.ChemicalName}  ${
                              e?.CasNumber ? `, ${e?.CasNumber}` : ""
                            }`}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="w-full flex justify-between items-center px-3 md:px-10 mb-4">
                      <p>No Data Found</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex md:w-[75%] w-[100%]  md:justify-between md:items-center md:max-h-[50vh] max-h-[100vh] overflow-y-scroll overflow-hidden lg:overflow-hidden h-[24rem] md:h-auto px-5 md:py-8 ">
              <Grid container spacing={2} mt={0}>
                {searchMenus?.length > 0 &&
                  searchMenus.map((menu, index) => (
                    <Grid
                      item
                      key={`${index}${menu.title}`}
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      // onClick={() => handleClick(index)}
                    >
                      <Link
                        href={`/product?category=industry&subcategory=${slugify(
                          menu?.title
                        )}`}
                        className={`cursor-pointer border p-5 box-border rounded-xs flex justify-between gap-4 h-[7rem] ${
                          index === menuIndex && "bg-red-600 text-white"
                        } hover:shadow-md`}
                        onClick={() => {
                          dispatch(setSelectedTab(0));
                          dispatch(
                            setSelected({
                              type: "Industry",
                              data: menu?.title,
                              step: 1,
                            })
                          );
                          dispatch(setCurrentStep(2));
                          close();
                        }}
                      >
                        <div>{menu.title}</div>
                        <div>
                          <img
                            src={menu?.icon}
                            alt="menu icon"
                            className=" h-[2rem] object-cover"
                          />
                        </div>
                      </Link>
                    </Grid>
                  ))}
              </Grid>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default SearchMenu;
